import React from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { YesNoRadioField } from "component/shared/yes-no-radio-field";
import { FullWidthDropdown } from "component/shared/full-width-dropdown";
import MuiAlert from "@mui/material/Alert";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { styles } from "./style";
import { connect } from "react-redux";
import { saveUser } from "redux/slice/authentication";
import { fetchPages } from "redux/slice/pages";
import { User } from "model/user";
import { canDisplayExternalLinksOnPropResultsAndDetails } from "redux/selector";
import { canShowPhotoLayoutOptions } from "redux/selector";
import { canShowSupplementalOnSeparatePage } from "redux/selector";
import { getPages } from "redux/selector";
import { LeadCaptureSidebarPositionType } from "type/lead-capture-sidebar-position";
import { ListingDetailPageLayoutType } from "type/listing-detail-page-layout";
import { PageType } from "type/page-type";
import { Switch } from "component/shared/switch";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import * as env from "shared/env";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
		canDisplayExternalLinksOnPropResultsAndDetails:
			canDisplayExternalLinksOnPropResultsAndDetails(state),
		canShowPhotoLayoutOptions: canShowPhotoLayoutOptions(state),
		canShowSupplementalOnSeparatePage: canShowSupplementalOnSeparatePage(state),
		pages: getPages(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
			fetchPages,
		},
		dispatch
	);

interface State {
	saving: boolean;
	settingShowDownPaymentAssistanceLinks: boolean;
	settingListingDetailPageStyle: ListingDetailPageLayoutType;
	settingShowScheduleShowing: boolean;
	settingShowMapLink: boolean;
	settingShowMortgageCalculator: boolean;
	settingShowWalkScore: boolean;
	settingShowSchools: boolean;
	listingDetailSidebarPosition: LeadCaptureSidebarPositionType;
	listingDetailStickyContent: boolean;
	listingDetailContiguous: boolean;
	listingDetailSimilarListings: boolean;
	listingDetailCarouselThumbnails: boolean;
	listingDetailCarouselCenterMode: boolean;
	listingDetailEmbedVirtualTour: boolean;
}

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		WithStyles<typeof styles> {
	user: User;
}

interface OwnProps {
	user: User;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		const settingShowDownPaymentAssistanceLinks =
			props.user.settingShowDownPaymentAssistanceLinks ||
			props.user.settingShowDownPaymentAssistanceLinks === undefined;
		this.state = {
			...props.user,
			saving: false,
			settingShowDownPaymentAssistanceLinks,
			listingDetailSidebarPosition:
				props.user.listingDetailSidebarPosition ||
				LeadCaptureSidebarPositionType.SIDEBAR_RIGHT,
			settingListingDetailPageStyle:
				props.user.settingListingDetailPageStyle ||
				ListingDetailPageLayoutType.STANDARD,
		};
	}

	public componentDidMount() {
		const { fetchPages } = this.props;
		fetchPages();
	}

	public save() {
		const { saveUser } = this.props;
		const user = { ...this.state };
		saveUser({user});
		this.setState({ saving: true });
	}

	private cancel() {
		const { user } = this.props;
		const { saving } = this.state;
		this.setState({
			...user,
			saving,
			settingShowDownPaymentAssistanceLinks: this.state.settingShowDownPaymentAssistanceLinks,
			settingListingDetailPageStyle: this.state.settingListingDetailPageStyle,
		});
	}

	private isDirty(): boolean {
		const { user } = this.props;
		return (
			this.state.settingShowDownPaymentAssistanceLinks !== (user.settingShowDownPaymentAssistanceLinks || user.settingShowDownPaymentAssistanceLinks === undefined) ||
			this.state.settingListingDetailPageStyle?.id !== (user.settingListingDetailPageStyle || ListingDetailPageLayoutType.STANDARD)?.id ||
			this.state.settingShowScheduleShowing !== user.settingShowScheduleShowing ||
			this.state.settingShowMapLink !== user.settingShowMapLink ||
			this.state.settingShowMortgageCalculator !== user.settingShowMortgageCalculator ||
			this.state.settingShowWalkScore !== user.settingShowWalkScore ||
			this.state.settingShowSchools !== user.settingShowSchools ||
			this.state.listingDetailSidebarPosition?.id !== (user.listingDetailSidebarPosition || LeadCaptureSidebarPositionType.SIDEBAR_RIGHT)?.id ||
			this.state.listingDetailStickyContent !== user.listingDetailStickyContent ||
			this.state.listingDetailContiguous !== user.listingDetailContiguous ||
			this.state.listingDetailSimilarListings !== user.listingDetailSimilarListings ||
			this.state.listingDetailCarouselThumbnails !== user.listingDetailCarouselThumbnails ||
			this.state.listingDetailCarouselCenterMode !== user.listingDetailCarouselCenterMode ||
			this.state.listingDetailEmbedVirtualTour !== user.listingDetailEmbedVirtualTour
		);
	}

	public render() {
		const {
			user,
			authLoading,
			authError,
			classes = {},
			canDisplayExternalLinksOnPropResultsAndDetails,
			canShowPhotoLayoutOptions,
			canShowSupplementalOnSeparatePage,
			pages,
		} = this.props;
		const {
			saving,
			settingListingDetailPageStyle,
			settingShowScheduleShowing,
			settingShowDownPaymentAssistanceLinks,
			settingShowMapLink,
			settingShowMortgageCalculator,
			settingShowSchools,
			settingShowWalkScore,
			listingDetailStickyContent,
			listingDetailSidebarPosition,
			listingDetailCarouselThumbnails,
			listingDetailCarouselCenterMode,
			listingDetailEmbedVirtualTour,
			listingDetailContiguous,
			listingDetailSimilarListings,
		} = this.state;

		const dirty = this.isDirty();

		const listingsPage =
			pages?.find(p => p.key === 'LISTING_SEARCH_FORM')
			?? pages?.find(p => p.type.id === PageType.LISTINGS.id);
		const listingsPageUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;

		const snackbar = (
			<Mui.Snackbar
				open={saving && !authLoading && !authError}
				message="Settings saved"
				autoHideDuration={6000}
				onClose={() => this.setState({ saving: false })}
				action={
					<Mui.IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={() => this.setState({ saving: false })}
					>
						<Icons.X fontSize="small" />
					</Mui.IconButton>
				}
			>
				<MuiAlert severity="info">
					<Mui.Typography>Settings saved</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		);

		const showSidebar = listingDetailSidebarPosition !== LeadCaptureSidebarPositionType.SIDEBAR_OFF;
		const handleSidebarToggle = (value: boolean) => {
			this.setState({
				listingDetailSidebarPosition: value
					? LeadCaptureSidebarPositionType.SIDEBAR_RIGHT
					: LeadCaptureSidebarPositionType.SIDEBAR_OFF,
			});
		};

		return (
			<DashboardLayout
				permitted={user && user.permissions.listingDetail}
				title="Listing Details"
				header={
					<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h1">Listing Details</Mui.Typography>
						{listingsPageUrl && (
							<PageHeaderLink href={listingsPageUrl}>View Search</PageHeaderLink>
						)}
					</Mui.Grid>
				}
			>
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					Customize the layout and content of listing detail pages on your website, including photo carousel style, additional listing content, and lead capture placement.
				</Mui.Typography>
				{!user.kestrelVersion.off && (
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
							{/* Photos & Videos */}
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>Photos &amp; Videos</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.subHeading}>Photo Carousel Layout</Mui.Typography>
									<Mui.RadioGroup
										value={listingDetailCarouselCenterMode ? "centered" : "standard"}
										onChange={e => this.setState({ listingDetailCarouselCenterMode: e.target.value === "centered" })}
									>
										<Mui.FormControlLabel
											value="standard"
											className={classes.carouselOption}
											control={<Mui.Radio color="secondary" />}
											style={{ marginBottom: 12 }}
											label={
												<>
													<Mui.Typography className={classes.carouselOptionTitle}>Standard</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.carouselOptionDescription}>Displays one large photo at a time.</Mui.Typography>
												</>
											}
										/>
										<Mui.FormControlLabel
											value="centered"
											className={classes.carouselOption}
											control={<Mui.Radio color="secondary" />}
											label={
												<>
													<Mui.Typography className={classes.carouselOptionTitle}>Centered Preview</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.carouselOptionDescription}>Shows the main photo centered with partial previews of the next and previous images.</Mui.Typography>
												</>
											}
										/>
									</Mui.RadioGroup>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Divider style={{ marginTop: 8, marginBottom: 8 }} />
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.subHeading} style={{ marginBottom: 20 }}>Other</Mui.Typography>
								<Mui.Box mb={2}>
									<Switch
										checked={listingDetailCarouselThumbnails}
										onChange={value => this.setState({ listingDetailCarouselThumbnails: value })}
										label="Show thumbnails under photo carousel"
										description="When enabled, thumbnail images will display below the main photo carousel for easy navigation. When disabled, website visitors can scroll through photos individually."
									/>
								</Mui.Box>
								{user.permissions.embedVirtualTours && (
									<Switch
										checked={listingDetailEmbedVirtualTour}
										onChange={value => this.setState({ listingDetailEmbedVirtualTour: value })}
										label="Show virtual tour player"
										description="When enabled, listings with a virtual tour link will display an embedded player directly on the property details page. When disabled, the embedded player will not display."
									/>
								)}
							</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Box>
				)}
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Grid container direction="column" spacing={2}>

							{/* Listing Information */}
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading}>Listing Information</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography className={classes.subHeading}>Additional Listing Content</Mui.Typography>
								<Mui.Typography className={classes.contentDescription}>
									Select which content should display in addition to listing data and description:
								</Mui.Typography>
								<Mui.Grid container direction="column">
									{user.permissions.similarListings && (
										<Mui.FormControlLabel
											control={<Mui.Checkbox checked={listingDetailSimilarListings} onChange={e => this.setState({ listingDetailSimilarListings: e.target.checked })} color="secondary" />}
											label="Similar Listings"
										/>
									)}
									<Mui.FormControlLabel
										control={<Mui.Checkbox checked={settingShowMapLink} onChange={e => this.setState({ settingShowMapLink: e.target.checked })} color="secondary" />}
										label="Area Map"
									/>
									<Mui.FormControlLabel
										control={<Mui.Checkbox checked={settingShowMortgageCalculator} onChange={e => this.setState({ settingShowMortgageCalculator: e.target.checked })} color="secondary" />}
										label="Mortgage Calculator"
									/>
									<Mui.FormControlLabel
										control={<Mui.Checkbox checked={settingShowWalkScore} onChange={e => this.setState({ settingShowWalkScore: e.target.checked })} color="secondary" />}
										label="Walk Score"
									/>
									<Mui.FormControlLabel
										control={<Mui.Checkbox checked={settingShowSchools} onChange={e => this.setState({ settingShowSchools: e.target.checked })} color="secondary" />}
										label="Schools"
									/>
									<Mui.Grid container alignItems="center">
										<Mui.FormControlLabel
											control={<Mui.Checkbox checked={settingShowScheduleShowing} onChange={e => this.setState({ settingShowScheduleShowing: e.target.checked })} color="secondary" />}
											label={'"Schedule a Showing" Form'}
											style={{ marginRight: 0 }}
										/>
										<Mui.Tooltip title={'"Schedule a Showing" form appears with the "Request Information" form. If turned off, only the "Request Information" form will show.'}>
											<span className={classes.infoIcon}><Icons.Info size={16} /></span>
										</Mui.Tooltip>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.Grid>
							{canShowSupplementalOnSeparatePage && (
								<>
									<Mui.Grid item>
										<Mui.Divider style={{ marginTop: 8, marginBottom: 8 }} />
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.Typography className={classes.subHeading}>Additional Content Layout</Mui.Typography>
									<Mui.RadioGroup
										value={listingDetailContiguous ? "tabs" : "below"}
										onChange={e => this.setState({ listingDetailContiguous: e.target.value === "tabs" })}
									>
										<Mui.FormControlLabel value="below" control={<Mui.Radio color="secondary" />} label="Display below listing data and description" />
										<Mui.FormControlLabel value="tabs" control={<Mui.Radio color="secondary" />} label="Display as tabs" />
									</Mui.RadioGroup>
								</Mui.Grid>
							</>
							)}
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
				{!user.kestrelVersion.off && (
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Grid container direction="column" spacing={2}>
							<Mui.Grid item>
								<Mui.Typography className={classes.sectionHeading} style={{ marginBottom: 20 }}>Lead Capture</Mui.Typography>
								<Mui.Box mb={2}>
									<Switch
										checked={showSidebar}
										onChange={handleSidebarToggle}
										label="Display lead capture sidebar alongside listing details"
										description="When enabled, a lead capture sidebar will display alongside your listing details content. We recommend disabling this if you already have a sidebar built into your page outside of the listing details content."
									/>
								</Mui.Box>
								{showSidebar && (
									<Mui.RadioGroup
										className={classes.indented}
										value={listingDetailSidebarPosition.id}
										onChange={e => this.setState({ listingDetailSidebarPosition: LeadCaptureSidebarPositionType.getByIdOrDefault(e.target.value) })}
									>
										<Mui.Typography variant="body2" className={classes.contentDescription}>Select placement below:</Mui.Typography>
										<Mui.FormControlLabel value={LeadCaptureSidebarPositionType.SIDEBAR_RIGHT.id} control={<Mui.Radio color="secondary" />} label="Right of content" />
										<Mui.FormControlLabel value={LeadCaptureSidebarPositionType.SIDEBAR_LEFT.id} control={<Mui.Radio color="secondary" />} label="Left of content" />
									</Mui.RadioGroup>
								)}
								<Switch
									checked={listingDetailStickyContent}
									onChange={value => this.setState({ listingDetailStickyContent: value })}
									label="Make lead capture actions sticky"
									description="When enabled, lead capture options will be fixed to the top of the browser window as visitors scroll. When disabled, lead capture options will scroll with the rest of the page content."
								/>
							</Mui.Grid>
							</Mui.Grid>
						</FullPageCard>
					</Mui.Box>
				)}
				<SettingsStickyFooter
					dirty={dirty}
					saving={saving}
					authError={authError}
					onSave={() => this.save()}
					onCancel={() => this.cancel()}
				/>
				{snackbar}
			</DashboardLayout>
		);
	}
}

export const SettingsListingDetailsPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
